// Single source of truth for the Products mega-menu, the /products index page,
// AND each single product page (rendered by ProductDetailPage at /products/<slug>).
//
// Each product is ONE page only — its own marketing site / app lives elsewhere
// (linked via `external`). Products are grouped by SECURITY FUNCTION so the menu
// scales as we add future products (e.g. EDR / XDR under Detection & Response).
//
// A product with `status: 'preview'` is on the roadmap (not yet generally
// available) and shows a "In preview" badge instead of an external launch link.

const cap = (title, desc) => ({ title, desc });
const step = (num, name, desc) => ({ step: num, name, desc });
const stat = (value, label) => ({ value, label });

export const productCategories = [
  // ---- Detection & Response ----
  {
    title: 'Threat Detection & Response',
    tag: 'Detect & Respond',
    blurb: 'Continuous monitoring, detection engineering, and analyst-led response.',
    items: [
      {
        label: 'Trinex Log 360',
        slug: 'trinex-log-360',
        href: '/products/trinex-log-360',
        icon: 'shield',
        status: 'live',
        menuDesc: 'Our managed SOC — 24/7 SIEM monitoring, threat detection, and analyst-led incident response.',
        hero: {
          eyebrow: 'Managed SOC · SIEM',
          title: 'Trinex Log 360',
          headline: 'Your 24/7 Security Operations Centre, delivered as a service.',
          desc:
            'Trinex Log 360 is our own managed SOC. We collect and correlate logs from across your estate, hunt for threats around the clock, and put certified analysts on every alert that matters — so detection and response never sleep.',
          badges: ['24/7 SOC', 'SIEM + SOAR', 'MITRE ATT&CK aligned', 'India & UK data residency'],
        },
        stats: [
          stat('24/7', 'Monitoring'),
          stat('SLA-Backed', 'Response'),
          stat('SIEM', 'Correlated'),
          stat('MITRE', 'ATT&CK aligned'),
        ],
        capabilities: [
          cap('24/7 SOC Monitoring', 'Certified analysts watch your environment around the clock — every alert triaged, investigated, and actioned by a human, not just a dashboard.'),
          cap('SIEM & Log Analytics', 'Centralised collection and correlation of logs from endpoints, servers, network, cloud, and SaaS — with retention that keeps you audit-ready.'),
          cap('Threat Detection & Hunting', 'Detection engineering mapped to MITRE ATT&CK, plus proactive threat hunting that surfaces what signature-based tools miss.'),
          cap('Incident Response', 'When something is real, our analysts move fast — containment, investigation, and coordinated recovery with clear communication throughout.'),
          cap('SOAR Automation', 'Playbook-driven automation enriches, prioritises, and de-duplicates alerts so response is measured in minutes, not hours.'),
          cap('Compliance-Ready Reporting', 'Continuous evidence and plain-English reporting that maps monitoring coverage to ISO 27001, SOC 2, PCI DSS, and more.'),
        ],
        how: [
          step('01', 'Onboard', 'We map your estate, connect log sources, and tune detections to your environment — no rip-and-replace.'),
          step('02', 'Monitor', 'Continuous collection and correlation across your surface, watched 24/7 by our SOC.'),
          step('03', 'Investigate', 'Every meaningful alert is triaged and investigated by a certified analyst.'),
          step('04', 'Respond & Report', 'We contain, guide recovery, and report — with continuous tuning to reduce noise over time.'),
        ],
        why: {
          title: 'Security that never sleeps — without the headcount.',
          body:
            'Building a mature 24/7 SOC in-house is slow and expensive. Trinex Log 360 gives you an expert team, mature tooling, and battle-tested playbooks for a predictable monthly cost.',
          bullets: [
            '24/7 coverage without hiring and retaining a night shift',
            'Faster detection and response with analysts on every alert',
            'Detection engineering aligned to MITRE ATT&CK',
            'Continuous compliance evidence for auditors and the board',
          ],
        },
        cta: { kind: 'contact', label: 'Talk to the SOC team' },
        seoTitle: 'Trinex Log 360 — Managed SOC & 24/7 Threat Detection | Trinex360',
        seoDesc:
          'Trinex Log 360 is a managed Security Operations Centre — 24/7 SIEM monitoring, MITRE ATT&CK detection engineering, and analyst-led incident response.',
      },
    ],
  },

  // ---- Exposure Management ----
  {
    title: 'Exposure Management',
    tag: 'See Your Attack Surface',
    blurb: 'Continuous, outside-in visibility of everything an attacker can see.',
    items: [
      {
        label: 'Trinex Exposure 360',
        slug: 'trinex-exposure-360',
        href: '/products/trinex-exposure-360',
        icon: 'crosshairs',
        status: 'live',
        menuDesc: 'External exposure monitoring — attack surface, dark web, and third-party risk in one board-ready score.',
        hero: {
          eyebrow: 'External Exposure Monitoring',
          title: 'Trinex Exposure 360',
          headline: 'See your organisation the way an attacker does.',
          desc:
            'Trinex Exposure 360 continuously monitors your external attack surface, the dark web, and your third parties — then turns what it finds into a clear, prioritised risk score your board can act on. Outside-in and fully agentless.',
          badges: ['Outside-in & agentless', 'Continuous 24/7', 'A–F risk grade', 'India & UK data residency'],
        },
        stats: [
          stat('20+', 'Intelligence sources'),
          stat('0', 'Agents to install'),
          stat('24/7', 'Continuous scanning'),
          stat('A–F', 'Executive risk grade'),
        ],
        capabilities: [
          cap('External Attack Surface', 'Continuously map your internet-facing footprint — domains, subdomains, exposed services, SSL/DNS posture, and known CVEs — exactly as an attacker would.'),
          cap('Dark Web & Ransomware', 'Monitor leak sites, ransomware blogs, and threat-actor channels for mentions of your brand, domains, and leaked data — before they become an incident.'),
          cap('Third-Party Risk', "Score your vendors' security posture from the outside in with a clear A–F grade, so you know which suppliers expand your risk."),
          cap('Business Risk Intelligence', 'An executive scoring layer that rolls noisy technical findings up into board-level signal across brand, ransomware, financial, and regulatory risk.'),
          cap('Credential & Secret Leaks', 'Detect exposed secrets, leaked credentials, and breached employee accounts across code, paste sites, and breach datasets.'),
          cap('Board-Ready Reports', 'Prioritised, plain-English reporting with a security score and grade — ready to share with leadership, auditors, and your board.'),
        ],
        how: [
          step('01', 'Define your scope', 'Add your domains and brands. Setup takes minutes — no agents, no installs, nothing to deploy.'),
          step('02', 'We monitor, continuously', 'Automated outside-in scans run on a schedule across your surface, the dark web, and your vendors.'),
          step('03', 'Act on what matters', 'Findings arrive prioritised and de-duplicated, with executive reporting for the people who need it.'),
        ],
        why: {
          title: 'From thousands of findings to one number your board understands.',
          body:
            'Technical findings are noisy. Trinex Exposure 360 rolls every finding up into an executive risk score and grade across the dimensions that matter to the business — so security and leadership finally speak the same language.',
          bullets: [
            'See exactly what an attacker sees from the public internet',
            'Nothing to install and no access to your network required',
            'Continuous scanning, not a once-a-year snapshot',
            'Data residency on India (.com) or UK (.co.uk) deployments',
          ],
        },
        cta: { kind: 'contact', label: 'Book a demo' },
        seoTitle: 'Trinex Exposure 360 — External Exposure Monitoring | Trinex360',
        seoDesc:
          'Trinex Exposure 360 continuously monitors your external attack surface, the dark web, and third-party risk — rolled into one board-ready security score.',
      },
    ],
  },

  // ---- Security Awareness ----
  {
    title: 'Security Awareness',
    tag: 'Train Your People',
    blurb: 'Turn your people into a resilient first line of defence.',
    items: [
      {
        label: 'Trinex Learn 360',
        slug: 'trinex-learn-360',
        href: '/products/trinex-learn-360',
        icon: 'graduation',
        status: 'live',
        external: { url: 'https://learn.trinex360.com/', label: 'Visit Learn 360' },
        menuDesc: 'Security awareness training — scenario-led courses, gamified simulations, and automatic certification.',
        hero: {
          eyebrow: 'Security Awareness Training',
          title: 'Trinex Learn 360',
          headline: 'Empower your team to build stronger security habits.',
          desc:
            'Trinex Learn 360 is our enterprise security-awareness platform — scenario-led training, gamified simulations, and progress visibility across your whole organisation. Built for CISOs, IT, HR, and compliance teams in multi-tenant environments.',
          badges: ['Multi-tenant', 'Gamified simulations', 'Automated certification', 'SSO & role-based access'],
        },
        stats: [
          stat('50+', 'Training & simulation modules'),
          stat('10–15m', 'Micro-lessons'),
          stat('Gamified', 'Scenario learning'),
          stat('Multi-tenant', 'Enterprise ready'),
        ],
        capabilities: [
          cap('Video-Based Learning', 'Engaging, interactive video content with progress tracking and automatic replay so learners actually finish their training.'),
          cap('Enterprise Tenant Management', 'Dedicated organisational spaces with pre-approved seat allocation, invitation-based onboarding, and complete data isolation.'),
          cap('Real-Time Analytics', 'Comprehensive reporting on progress, completion rates, and compliance status — visibility for every stakeholder.'),
          cap('Automated Certification', 'Generate certificates automatically on completion, with customisable templates and validation.'),
          cap('Automated Retraining', 'Schedule automatic retraining cycles (1–2 years) to maintain compliance and keep knowledge fresh.'),
          cap('Role-Based Access & SSO', 'Granular permissions for admins and employees, with secure single sign-on integration.'),
        ],
        how: [
          step('01', 'Set up your organisation', 'Create your secure tenant space, invite team members, and configure training requirements.'),
          step('02', 'Assign training modules', 'Select from our library and assign relevant training and simulations to your employees.'),
          step('03', 'Track & certify', 'Monitor progress in real time and automatically issue certificates on completion.'),
        ],
        why: {
          title: 'Your people are the perimeter.',
          body:
            'Most incidents start with a human decision. Scenario-led modules and gamified simulations build the reflexes that slide-only training never does — measurably improving phishing, fraud, and incident-response readiness.',
          bullets: [
            'Higher completion rates from short, scenario-led sessions',
            'Faster threat recognition through gamified simulations',
            'Consistent, measurable behaviour via role-based journeys',
            'Multi-tenant controls and governance for simple rollout',
          ],
        },
        cta: { kind: 'external', label: 'Book a demo' },
        seoTitle: 'Trinex Learn 360 — Enterprise Security Awareness Training | Trinex360',
        seoDesc:
          'Trinex Learn 360 is an enterprise security-awareness platform — scenario-led training, gamified simulations, automated certification, and multi-tenant management.',
      },
    ],
  },
];

// Flat list of every product, tagged with its parent category title.
export const allProducts = productCategories.flatMap((c) =>
  c.items.map((it) => ({ ...it, category: c.title }))
);

export const getProductBySlug = (slug) =>
  allProducts.find((product) => product.slug === slug);
